// Central skin registry — single source of truth shared between V2 Store,
// Profile (My Skins + skinned hero), and V1 SocialCard.
//
// Names + prices match backend (server.py SKIN_CATALOG):
//   bronze  · 10
//   elite   · 20
//   legend  · 40
//   phantom · 60

import {
  Award, Shield, Crown, Ghost, Sun, Sparkles, Bird, Flame,
} from "lucide-react";

export const SKINS = {
  bronze: {
    id: "bronze",
    name: "Bronze",
    price: 10,
    rarity: "common",
    tagline: "Start the climb",
    icon: Award,
    // Card theme — used by SkinnedProfileCard + SocialCard skin overlay.
    bg: "linear-gradient(135deg, #1A0F05 0%, #3B1F08 35%, #6B3A14 70%, #1A0F05 100%)",
    accent: "#F59E0B",
    accentSoft: "rgba(245,158,11,0.25)",
    border: "rgba(245,158,11,0.45)",
    shadow: "0 0 32px rgba(245,158,11,0.18)",
    text: "#FBBF24",
    textMuted: "rgba(251,191,36,0.7)",
    label: "BRONZE",
    pattern: "rays-warm",
  },
  elite: {
    id: "elite",
    name: "Elite",
    price: 20,
    rarity: "rare",
    tagline: "Sharpen the edge",
    icon: Shield,
    bg: "linear-gradient(135deg, #03101F 0%, #0B2540 40%, #1E40AF 70%, #03101F 100%)",
    accent: "#3B82F6",
    accentSoft: "rgba(59,130,246,0.25)",
    border: "rgba(96,165,250,0.5)",
    shadow: "0 0 32px rgba(59,130,246,0.22)",
    text: "#93C5FD",
    textMuted: "rgba(147,197,253,0.7)",
    label: "ELITE",
    pattern: "diagonals-cool",
  },
  legend: {
    id: "legend",
    name: "Legend",
    price: 40,
    rarity: "epic",
    tagline: "Earn your gold",
    icon: Crown,
    bg: "linear-gradient(135deg, #1A0F02 0%, #3B2503 30%, #B45309 65%, #FCD34D 100%)",
    accent: "#FCD34D",
    accentSoft: "rgba(252,211,77,0.3)",
    border: "rgba(252,211,77,0.6)",
    shadow: "0 0 36px rgba(252,211,77,0.32)",
    text: "#FFFBEB",
    textMuted: "rgba(255,251,235,0.78)",
    label: "LEGEND",
    pattern: "starburst-gold",
  },
  phantom: {
    id: "phantom",
    name: "Phantom",
    price: 60,
    rarity: "legendary",
    tagline: "Vanish with the win",
    icon: Ghost,
    bg: "linear-gradient(135deg, #050008 0%, #1A0530 35%, #3B0764 65%, #050008 100%)",
    accent: "#A78BFA",
    accentSoft: "rgba(167,139,250,0.3)",
    border: "rgba(167,139,250,0.55)",
    shadow: "0 0 40px rgba(167,139,250,0.35)",
    text: "#DDD6FE",
    textMuted: "rgba(221,214,254,0.72)",
    label: "PHANTOM",
    pattern: "smoke-violet",
  },
  // ─── National-team series (iter 53) ──────────────────────────────────────
  egypt_pharaohs: {
    id: "egypt_pharaohs",
    name: "Egypt Pharaohs",
    price: 10,
    rarity: "common",
    tagline: "Rule the Nile",
    icon: Sun,
    // Pharaoh gold over deep amber rays.
    bg: "linear-gradient(135deg, #1F0F00 0%, #4A2400 30%, #8A4B00 60%, #B8741C 80%, #1F0F00 100%)",
    accent: "#F5A623",
    accentSoft: "rgba(245,166,35,0.28)",
    border: "rgba(245,166,35,0.55)",
    shadow: "0 0 34px rgba(245,166,35,0.28)",
    text: "#FFD27A",
    textMuted: "rgba(255,210,122,0.72)",
    label: "PHARAOHS",
    pattern: "rays-warm",
  },
  algeria_fennecs: {
    id: "algeria_fennecs",
    name: "Algeria Fennecs",
    price: 20,
    rarity: "rare",
    tagline: "Desert Warriors",
    icon: Sparkles,
    // Algerian green with white silver shine sweep.
    bg: "linear-gradient(135deg, #013220 0%, #036635 30%, #E8F5EC 55%, #036635 80%, #013220 100%)",
    accent: "#00A86B",
    accentSoft: "rgba(0,168,107,0.28)",
    border: "rgba(0,168,107,0.55)",
    shadow: "0 0 32px rgba(0,168,107,0.28)",
    text: "#F4F4F5",
    textMuted: "rgba(244,244,245,0.72)",
    label: "FENNECS",
    pattern: "diagonals-cool",
  },
  saudi_falcons: {
    id: "saudi_falcons",
    name: "Saudi Falcons",
    price: 40,
    rarity: "epic",
    tagline: "Soar Above All",
    icon: Bird,
    // Saudi green with golden-yellow ray burst.
    bg: "linear-gradient(135deg, #001F0E 0%, #04331A 25%, #006C35 55%, #C5A52A 85%, #001F0E 100%)",
    accent: "#FFD700",
    accentSoft: "rgba(255,215,0,0.3)",
    border: "rgba(255,215,0,0.6)",
    shadow: "0 0 36px rgba(255,215,0,0.32)",
    text: "#FFF7CC",
    textMuted: "rgba(255,247,204,0.78)",
    label: "FALCONS",
    pattern: "starburst-gold",
  },
  morocco_atlas: {
    id: "morocco_atlas",
    name: "Morocco Atlas Lions",
    price: 60,
    rarity: "legendary",
    tagline: "Roar of the Atlas",
    icon: Flame,
    // Moroccan red with green pentagram glow sweeping to green edge.
    bg: "linear-gradient(135deg, #2B0000 0%, #8B0F00 25%, #C1272D 55%, #00603A 85%, #2B0000 100%)",
    accent: "#00A86B",
    accentSoft: "rgba(0,168,107,0.3)",
    border: "rgba(193,39,45,0.65)",
    shadow: "0 0 40px rgba(193,39,45,0.38)",
    text: "#FDECEC",
    textMuted: "rgba(253,236,236,0.78)",
    label: "ATLAS LIONS",
    pattern: "smoke-violet",
  },
};

export const SKIN_LIST = [
  "bronze", "elite", "legend", "phantom",
  "egypt_pharaohs", "algeria_fennecs", "saudi_falcons", "morocco_atlas",
].map((id) => SKINS[id]);

export function getSkin(skinId) {
  if (!skinId) return null;
  return SKINS[skinId] || null;
}

// Returns inline-style props you can spread onto a card div to apply the skin's
// look without rebuilding the whole card. Used by V1 SocialCard for parity.
export function skinCardStyle(skinId) {
  const sk = getSkin(skinId);
  if (!sk) return null;
  return {
    background: sk.bg,
    boxShadow: sk.shadow,
    borderColor: sk.border,
  };
}

// Decorative pattern overlay rendered as a positioned span — purely visual.
export function SkinPatternOverlay({ skinId, className = "" }) {
  const sk = getSkin(skinId);
  if (!sk) return null;
  // Pattern variants — CSS-only so the bundle stays lean.
  let bg;
  if (sk.pattern === "rays-warm") {
    bg = "repeating-conic-gradient(from 0deg at 50% 100%, rgba(252,211,77,0) 0deg, rgba(252,211,77,0.06) 4deg, rgba(252,211,77,0) 8deg, rgba(252,211,77,0) 16deg)";
  } else if (sk.pattern === "diagonals-cool") {
    bg = "repeating-linear-gradient(135deg, rgba(147,197,253,0.05) 0px, rgba(147,197,253,0.05) 2px, transparent 2px, transparent 18px)";
  } else if (sk.pattern === "starburst-gold") {
    bg = "radial-gradient(circle at 50% 30%, rgba(252,211,77,0.18) 0%, transparent 55%), repeating-conic-gradient(from 30deg at 50% 30%, rgba(252,211,77,0) 0deg, rgba(252,211,77,0.08) 6deg, rgba(252,211,77,0) 12deg)";
  } else if (sk.pattern === "smoke-violet") {
    bg = "radial-gradient(ellipse at 70% 30%, rgba(167,139,250,0.25) 0%, transparent 50%), radial-gradient(ellipse at 20% 80%, rgba(196,181,253,0.15) 0%, transparent 50%)";
  }
  return <span aria-hidden className={`absolute inset-0 pointer-events-none ${className}`} style={{ background: bg, opacity: 0.9 }} />;
}
